import { ComponentLoaderFunction, IComponentsRegistrar } from '@wix/thunderbolt-components-loader'
import { named, withDependencies } from '@wix/thunderbolt-ioc'
import { Experiments, ExperimentsSymbol, SiteFeatureConfigSymbol } from '@wix/thunderbolt-symbols'
import { ReactLoaderForOOISymbol, name } from './symbols'
import { OOIComponentData, OOIComponentLoader, OOISiteConfig } from './types'
import { OOIReporterSymbol, Reporter } from './reporting'
import { transform } from 'lodash'
import { getCompClassType } from '@wix/thunderbolt-commons'

const TPA_WIDGET_NATIVE_COMP_TYPE = 'tpaWidgetNative'

export const ooiComponentsRegistrar = withDependencies(
	[named(SiteFeatureConfigSymbol, name), ExperimentsSymbol, ReactLoaderForOOISymbol, OOIReporterSymbol],
	(
		{ ooiComponentsData }: OOISiteConfig,
		experiments: Experiments,
		ooiComponentsLoader: OOIComponentLoader,
		reporter: Reporter
	): IComponentsRegistrar => {
		const isOOIComponentsRegistrar =
			experiments['specs.thunderbolt.ooi_register_with_components_registrar'] &&
			experiments['specs.thunderbolt.useWidgetIdAsNamespaceForLoadable']
		const isSSR = !process.env.browser

		if (!isOOIComponentsRegistrar) {
			return {
				getComponents: () => ({}),
			}
		}

		const loadComponent = async ({ widgetId }: { widgetId: OOIComponentData['widgetId'] }) => {
			const { component, loadableReady, chunkLoadingGlobal } = await ooiComponentsLoader.getComponent(widgetId)
			if (isSSR) {
				return { default: component }
			}

			const { sentryDsn, isLoadable } = ooiComponentsData[widgetId]

			if (!component) {
				reporter.reportError(new Error('component is not exported'), sentryDsn, {
					tags: { phase: 'ooi component resolution' },
				})
			}

			/**
			 * loadableReady should come from the OOI bundle to share the same registry with its internal loadable functions:
			 * slack: https://wix.slack.com/archives/C026GJZFTBJ/p1634912220010100
			 * chunkLoadingGlobal is the namespcaes of the OOI component to fix issues with multiple loadable apps on the same page
			 * issue: https://github.com/gregberge/loadable-components/pull/701
			 */
			if (isLoadable && loadableReady && chunkLoadingGlobal) {
				await new Promise((resolve) => loadableReady(resolve, { chunkLoadingGlobal, namespace: widgetId }))
			}

			return { default: component }
		}

		const components = transform(
			ooiComponentsData,
			(res: Record<string, ComponentLoaderFunction<any>>, _, widgetId) => {
				res[getCompClassType(TPA_WIDGET_NATIVE_COMP_TYPE, widgetId)] = () => loadComponent({ widgetId })
			}
		)
		return {
			getComponents: () => components,
		}
	}
)
